import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useSearchParams } from 'react-router-dom';
import ReportCard from 'component/reports/ReportCard';
import { getSelectedLanguage } from 'utils/language';
import { reportService } from '../../services/reportService';

const normalizeSectors = (value) => {
  const input = Array.isArray(value) ? value : (value ? [value] : []);
  return input
    .map((sector) => {
      if (typeof sector === 'string') return sector;
      if (sector && typeof sector === 'object') return sector.name || sector.slug || '';
      return '';
    })
    .filter(Boolean);
};

const mapReport = (r = {}, t) => ({
  id: r.id ?? r.report_id ?? r._id,
  title: r.title || r.name || t("reports.untitled"),
  summary: r.summary || r.description || '',
  image_url: r.hero_image_url || r.image_url || r.cover_image,
  match_rate: r.match_rate ?? r.score ?? null,
  sectors: normalizeSectors(r.sectors || (r.sector ? [r.sector] : [])),
  recent_at: r.created_at || r.published_at || r.updated_at,
  reading_time_minutes: r.reading_time_minutes || r.read_time_minutes || r.reading_minutes || null,
});

const ReportSearch = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const searchType = searchParams.get('type') || 'text';

  const [reports, setReports] = useState([]);
  const [aiSummary, setAiSummary] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (!searchQuery.trim()) {
        setReports([]);
        setAiSummary('');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await reportService.searchReports({
          query: searchQuery,
          type: searchType,
          limit: 12,
          language: getSelectedLanguage(),
        });

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : [];

        setReports(list.map((item) => mapReport(item, t)));
        setAiSummary(typeof data?.ai_summary === 'string' ? data.ai_summary : '');
      } catch (e) {
        setError(e?.message || t("reports.search.error"));
        setReports([]);
        setAiSummary('');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [searchQuery, searchType, t]);

  const heading = useMemo(() => {
    if (!searchQuery.trim()) return t("reports.search.title");
    return t("reports.search.resultsFor", { query: searchQuery });
  }, [searchQuery, t]);

  return (
    <div className="main__content__box">
      <div className="container-fluid">
        <div className="content_wrap">
          <h4 className="text-white fw-semibold mb-3">{heading}</h4>

          {loading && <div className="text-muted mb-3">{t("reports.search.searching")}</div>}
          {error && <div className="text-danger mb-3">{error}</div>}
          {!loading && !error && !searchQuery.trim() && (
            <div className="text-muted mb-3">{t("reports.search.enterKeyword")}</div>
          )}

          {!loading && !error && searchType === 'ai' && aiSummary && (
            <div className="bg_glassEffect p-4 rounded-4 text-white mb-4">
              <h6 className="fw-semibold mb-2">{t("reports.aiSummary")}</h6>
              <p className="mb-0">{aiSummary}</p>
            </div>
          )}

          {!loading && !error && reports.length === 0 && searchQuery.trim() && (
            <div className="text-muted">{t("reports.search.noReports")}</div>
          )}

          <div className="row g-4">
            {reports.map((report, index) => (
              <div className="col-md-6 col-lg-4" key={report.id || index}>
                <ReportCard
                  value={index + 1}
                  title={report.title}
                  summary={report.summary}
                  _id={report.id}
                  progress={report.match_rate}
                  image_url={report.image_url}
                  sectors={report.sectors}
                  created_at={report.recent_at}
                  reading_time_minutes={report.reading_time_minutes}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportSearch;
